<template>
  <div
    class="counselor-grid"
    id="counselor-grid"
    :class="[resolvedRole, { 'counselor-grid--slim': slimTemplate }]"
  >
    <counselor-filter
      :country="adminMarket"
      :is-city-page="isCityPage === 'true'"
      :role="resolvedRole"
    />
    <div class="counselor-grid__wrapper">
      <counselor-grid-header
        :role="resolvedRole"
        :show-sort-by="isTherapy || isLawyer || isFuneral"
      />
      <div
        class="counselor-grid__cards"
        v-if="!isLoading && !isFiltering"
        :class="view"
      >
        <p
          class="counselor-grid__grid-break"
          v-if="noMatchingUsersFilteredByPromptGridBreakText"
          v-html="noMatchingUsersFilteredByPromptGridBreakText"
        />

        <p
          class="counselor-grid__grid-break"
          v-if="matchingUsersPlaceGridBreakText"
          v-html="matchingUsersPlaceGridBreakText"
        />
        <counselor-archive-card
          v-for="(counselor, index) in matchingUsers"
          :key="counselor.id"
          :counselor="counselor"
          :data-cy="`counselor-grid-card-${index}`"
          :is-lawyer="isLawyer"
          :is-list="view === 'list'"
          :is-therapist="isTherapy"
          :role="resolvedRole"
          :show-competence-areas="showCompetenceAreas"
          :show-phone-number="showPhoneNumber"
        />
        <p
          class="counselor-grid__grid-break"
          v-if="partiallyMatchingUsersGridBreakText"
          v-html="partiallyMatchingUsersGridBreakText"
        />
        <counselor-archive-card
          v-for="counselor in partiallyMatchingUsers"
          :key="counselor.id"
          :counselor="counselor"
          :is-lawyer="isLawyer"
          :is-list="view === 'list'"
          :is-therapist="isTherapy"
          :role="resolvedRole"
          :show-competence-areas="showCompetenceAreas"
          :show-phone-number="showPhoneNumber"
        />
        <p
          class="counselor-grid__grid-break"
          v-if="noMatchingUsersGridBreakText"
          v-html="noMatchingUsersGridBreakText"
        />
        <counselor-archive-card
          v-for="counselor in notMatchingUsers"
          :key="counselor.id"
          :counselor="counselor"
          :is-lawyer="isLawyer"
          :is-list="view === 'list'"
          :is-therapist="isTherapy"
          :role="resolvedRole"
          :show-competence-areas="showCompetenceAreas"
          :show-phone-number="showPhoneNumber"
        />
      </div>

      <counselor-grid-loader
        :is-therapist="isTherapy"
        :loading="isLoading || isFiltering"
      />
    </div>
    <verified-modal v-if="isTherapy" />
  </div>
</template>

<script>
import { MEETING_TYPE_KEYS } from 'Constants/meetingTypes';
import { capitalizeFirstLetter } from 'Library/stringFormatHelpers';
import { useCityTermLocalStorage } from 'Main/composables/useCityTermLocalStorage';
import { useCounselorGridFilter } from 'Main/composables/useCounselorGridFilter';
import {
  ROLE,
  resolveApiRole,
  useRoleTermLocalStorage,
} from 'Main/composables/useRoleTermLocalStorage';
import { hasTherapyRole } from 'Main/contact-form/constants';
import VerifiedModal from 'Main/verified-by-lavendla/components/VerifiedModal.vue';
import {
  getUserAvailableTimes,
  getUserBookedInTheLatest72Hours,
  getUserMeetingTimes,
  getUserReturningCustomer,
  getUserYearsOfExperience,
  prepareCounselorLoadParams,
} from 'Services/counselor-grid-user-service';
import { sprintf } from 'sprintf-js';
import { computed, watch } from 'vue';
import { mapGetters, mapState, useStore } from 'vuex';
import CounselorArchiveCard from './CounselorArchiveCard.vue';
import CounselorFilter from './CounselorFilter.vue';
import CounselorGridHeader from './CounselorGridHeader.vue';
import CounselorGridLoader from './CounselorGridLoader.vue';

export default {

  beforeUnmount() {
    window.removeEventListener(
      'city-storage-updated-event',
      this.loadCounselors
    );
  },

  components: {
    CounselorArchiveCard,
    CounselorFilter,
    CounselorGridHeader,
    CounselorGridLoader,
    VerifiedModal,
  },

  computed: {
    ...mapState('users', ['users', 'isLoading', 'filteredByPrompt']),
    ...mapState('counselorGrid', [
      'isFiltering',
      'view',
      'highestYearsOfExperience',
    ]),
    ...mapGetters('counselorGrid', ['numOfSetFilters']),

    hasFilteredPlace() {
      return this.filteredPlace?.location;
    },

    hasLabels() {
      return this.label && this.pluralLabel;
    },

    hasMatchingUsers() {
      return this.matchingUsers.length > 0;
    },

    isFuneral() {
      return this.resolvedRole === ROLE.GENERIC;
    },

    isLawyer() {
      return this.resolvedRole === ROLE.LAWYER;
    },

    isTherapy() {
      return hasTherapyRole(this.resolvedRole);
    },

    matchingUsersPlaceGridBreakText() {
      if (!this.hasLabels || !this.hasMatchingUsers || !this.hasFilteredPlace) {
        return '';
      }

      return sprintf(
        this.texts.counselorsInYourArea,
        this.matchingUsers.length,
        this.matchingUsers.length === 1 ? this.label : this.pluralLabel
      );
    },

    noMatchingUsersFilteredByPromptGridBreakText() {
      if (this.filteredByPrompt && this.users.length === 0) {
        return this.texts.noResultsFromPromptFilter;
      }

      return '';
    },

    noMatchingUsersGridBreakText() {
      if (!this.notMatchingUsers.length) {
        return '';
      }

      if (!this.matchingUsers.length && !this.partiallyMatchingUsers.length) {
        return this.texts.noResultsMoreBelow;
      }

      if (this.isTherapy) {
        return sprintf(
          this.texts.someResultsMoreBelowTherapy,
          capitalizeFirstLetter(this.pluralLabel)
        );
      }

      return this.isLawyer
        ? this.texts.someResultsMoreBelowLaw
        : this.texts.someResultsMoreBelowFuneral;
    },

    partiallyMatchingUsersGridBreakText() {
      if (!this.hasLabels || !this.partiallyMatchingUsers.length) {
        return '';
      }

      if (
        !this.hasMatchingUsers &&
        this.filteredMeetingTypes?.includes(MEETING_TYPE_KEYS.IN_PERSON)
      ) {
        return sprintf(
          this.texts.counselorsInOtherAreasWithNoMatchingUsersAndInPersonIsSet,
          this.pluralLabel,
          this.partiallyMatchingUsers.length
        );
      }

      return !this.hasMatchingUsers
        ? sprintf(
            this.texts.counselorsInOtherAreasWithNoMatchingUsers,
            this.label
          )
        : sprintf(this.texts.counselorsInOtherAreas, this.pluralLabel);
    },

    // Drives both the advisors API call and the card presentation so the
    // fetched advisor type and how the grid renders never diverge.
    resolvedRole() {
      return resolveApiRole(this.role, this.roleTerm);
    },

    showCompetenceAreas() {
      return this.isTherapy || (!this.competence && this.isLawyer);
    },

    showMatchingUsersGridBreak() {
      return (
        this.label &&
        this.pluralLabel &&
        this.matchingUsers.length > 0 &&
        this.filteredPlace?.location
      );
    },
  },

  created() {
    if (this.competence) {
      this.$store.commit('counselorGrid/setPresetCompetence', this.competence);
    }
  },

  data() {
    const getProp = (prop) => {
      return this[`${prop}Prop`] || this.$root.$data[prop];
    };

    return {
      adminMarket: getProp('adminMarket'),
      competence: getProp('competence'),
      displayPhoneNumber: getProp('displayPhoneNumber'),
      isCityPage: getProp('isCityPage'),
      label: getProp('label') || window.theme_vars.counselorGridTexts.therapist,
      pluralLabel:
        getProp('pluralLabel') ||
        window.theme_vars.counselorGridTexts.therapists,
      role: getProp('role'),
      showPhoneNumber: !!this.displayPhoneNumber,
      siteId: getProp('siteId'),
      slimTemplate: getProp('slimTemplate'),
      texts: theme_vars.counselorGridTexts,
    };
  },

  methods: {
    loadCounselors() {
      const cityTerm = this.getCityTermFromLocalStorage();
      const params = prepareCounselorLoadParams(
        cityTerm,
        this.city,
        this.resolvedRole,
        this.competence,
        this.adminMarket,
        this.siteId
      );

      this.$store.dispatch('users/getUsers', params);
    },
  },

  mounted() {
    this.saveRoleTermToLocalStorage(this.role);
    this.loadCounselors();
    window.addEventListener('city-storage-updated-event', this.loadCounselors);
  },
  name: 'CounselorGrid',

  props: {
    adminMarketProp: { default: null, type: String },
    competenceProp: { default: null, type: String },
    displayPhoneNumberProp: { default: false, type: Boolean },
    isCityPageProp: { default: false, type: Boolean },
    labelProp: { default: null, type: String },
    pluralLabelProp: { default: null, type: String },
    roleProp: { default: null, type: String },
    siteIdProp: { default: null, type: String },
    slimTemplateProp: { default: false, type: Boolean },
  },

  setup() {
    const store = useStore();
    const {
      cityTerm,
      saveCityTermToLocalStorage,
      getCityTermFromLocalStorage,
    } = useCityTermLocalStorage();
    const { roleTerm, saveRoleTermToLocalStorage } = useRoleTermLocalStorage();

    const preparedUsersGetter = () => {
      const users = store.state.users.users;
      if (!users) return [];
      const highestYearsOfExperience =
        store.state.counselorGrid.highestYearsOfExperience;
      const setHighestYearsOfExperience = (val) =>
        store.commit('counselorGrid/setHighestYearsOfExperience', val);
      return users.map((user) => {
        if (!user.schedule) return [];
        return {
          ...user,
          available_times: getUserAvailableTimes(user),
          booked_in_the_latest_72_hours: getUserBookedInTheLatest72Hours(user),
          meeting_types: getUserMeetingTimes(user),
          returning_customer: getUserReturningCustomer(user),
          years_of_experience: getUserYearsOfExperience(
            user,
            highestYearsOfExperience,
            setHighestYearsOfExperience
          ),
        };
      });
    };

    const {
      latestSearchedForCities,
      matchingUsers,
      partiallyMatchingUsers,
      notMatchingUsers,
      currentSite,
      role,
      shouldLogFilters,
      loggedFilters,
      cities,
      filteredAccreditations,
      filteredCompetenceAreas,
      filteredGender,
      filteredLanguages,
      filteredLocation,
      filteredPlace,
      filteredMeetingTypes,
      filteredTargetGroups,
      filteredTitles,
      filteredYearsOfExperience,
      filteredAvailableTimes,
      filteredAllowedDistance,
      sortBy,
      googleMapsIsAvailable,
      setIsFiltering,
      setSortBy,
      filterUsers,
      logFilterChange,
      updateUrlWithFilters,
      applyFiltersFromUrl,
    } = useCounselorGridFilter(preparedUsersGetter);

    // Watch users changes to re-filter (replaces the mixin's preparedUsers watcher)
    const users = computed(() => store.state.users.users);
    watch(users, () => {
      filterUsers();
      shouldLogFilters.value = true;
    });

    return {
      applyFiltersFromUrl,
      cities,
      cityTerm,
      currentSite,
      filteredAccreditations,
      filteredAllowedDistance,
      filteredAvailableTimes,
      filteredCompetenceAreas,
      filteredGender,
      filteredLanguages,
      filteredLocation,
      filteredMeetingTypes,
      filteredPlace,
      filteredTargetGroups,
      filteredTitles,
      filteredYearsOfExperience,
      filterUsers,
      getCityTermFromLocalStorage,
      googleMapsIsAvailable,
      latestSearchedForCities,
      logFilterChange,
      loggedFilters,
      matchingUsers,
      notMatchingUsers,
      partiallyMatchingUsers,
      roleTerm,
      saveCityTermToLocalStorage,
      saveRoleTermToLocalStorage,
      setIsFiltering,
      setSortBy,
      shouldLogFilters,
      sortBy,
      updateUrlWithFilters,
    };
  },
};
</script>
